import { Link } from "react-router-dom";
import { Mail, Linkedin, Instagram } from "lucide-react";
import { LINKEDIN, INSTAGRAM } from "@/lib/seo-schemas";
import { COMPANY, regions, REGIONAL_CONTACTS } from "@/data/clientsData";
import StagehookLogo from "@/components/icons/StagehookLogo";
import { openCookieSettings } from "@/components/CookieBanner";

const Footer = () => {
  return (
    <footer
      data-testid="footer"
      className="mt-32 pt-20 pb-10 border-t"
      style={{ background: "var(--bg-soft)", borderColor: "var(--line)" }}
    >
      <div className="shg-container">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-10">
          {/* Brand */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <StagehookLogo size={44} radius={8} />
              <div className="font-display text-2xl">STAGEHOOK GLOBAL</div>
            </div>
            <p className="text-[var(--text-soft)] text-[15px] leading-relaxed max-w-md mb-6">
              {COMPANY.description}
            </p>
          </div>

          {/* Regions */}
          <div className="lg:col-span-4">
            <div className="font-mono text-[10px] tracking-[0.25em] text-[var(--text-muted)] mb-5">
              EXPO BOOTH BUILDERS · REGIONS
            </div>
            <div className="flex flex-col gap-3">
              {regions.map((r) => (
                <Link
                  key={r.slug}
                  to={`/region/${r.slug}`}
                  data-testid={`footer-region-${r.slug}`}
                  className="text-sm text-[var(--text-soft)] hover:text-[var(--red)] transition-colors flex items-center gap-3"
                >
                  <span className="text-lg">{r.flag}</span>
                  <div>
                    <div className="font-medium">{r.name}</div>
                    <div className="text-[10px] text-[var(--text-muted)] font-mono tracking-[0.15em]">
                      {r.tagline}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <div className="font-mono text-[10px] tracking-[0.25em] text-[var(--text-muted)] mb-5">
              CUSTOMER SUPPORT · OFFICES
            </div>
            <ul className="space-y-4 text-sm text-[var(--text-soft)] mb-6">
              {REGIONAL_CONTACTS.map((rc) => (
                <li key={rc.region}>
                  <a
                    href={`tel:${rc.dial}`}
                    data-testid={`footer-phone-${rc.region.toLowerCase()}`}
                    className="flex items-start gap-3 hover:text-[var(--red)] transition-colors group"
                  >
                    <span className="text-base leading-none mt-0.5">{rc.flag}</span>
                    <span>
                      <span className="block text-[10px] font-mono tracking-[0.2em] text-[var(--text-muted)] mb-0.5">
                        {rc.region.toUpperCase()}
                      </span>
                      <span className="font-medium block">{rc.phone}</span>
                      {rc.address && (
                        <span className="block text-[11px] leading-snug text-[var(--text-muted)] mt-1 max-w-[220px] group-hover:text-[var(--text-soft)] transition-colors">
                          {rc.address}
                        </span>
                      )}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
            <ul className="space-y-3 text-sm text-[var(--text-soft)] pt-4 border-t" style={{ borderColor: "var(--line)" }}>
              <li className="flex items-start gap-3">
                <Mail size={15} className="text-[var(--red)] mt-0.5" />
                <span>{COMPANY.email}</span>
              </li>
              <li>
                <a href={LINKEDIN} target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin" className="flex items-center gap-3 hover:text-[var(--red)] transition-colors"><Linkedin size={15} className="text-[var(--red)]" /> Follow us on LinkedIn</a>
              </li>
              <li>
                <a href={INSTAGRAM} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="flex items-center gap-3 hover:text-[var(--red)] transition-colors"><Instagram size={15} className="text-[var(--red)]" /> Follow us on Instagram</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-[var(--line)] flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs font-mono text-[var(--text-muted)]">
            © {new Date().getFullYear()} Stagehook Global. All rights reserved. · <Link to="/privacy" data-testid="footer-privacy" className="hover:text-[var(--red)]">Privacy</Link> · <Link to="/terms" data-testid="footer-terms" className="hover:text-[var(--red)]">Terms</Link> · <button type="button" data-testid="footer-cookie-settings" onClick={openCookieSettings} className="hover:text-[var(--red)]">Cookie settings</button>
          </p>
          <p className="text-xs font-mono text-[var(--text-muted)] flex flex-wrap gap-4">
            <Link to="/stand-builder" data-testid="footer-cities" className="hover:text-[var(--red)]">Cities</Link>
            <Link to="/booth" data-testid="footer-booths" className="hover:text-[var(--red)]">Booth designs</Link>
            <Link to="/show" data-testid="footer-shows" className="hover:text-[var(--red)]">Upcoming shows</Link>
            <Link to="/blog" data-testid="footer-blog" className="hover:text-[var(--red)]">Blog</Link>
            <Link to="/case-studies" data-testid="footer-case-studies" className="hover:text-[var(--red)]">Case studies</Link>
            <Link to="/tools/exhibition-stand-cost-calculator" data-testid="footer-calculator" className="hover:text-[var(--red)]">Cost calculator</Link>
            <Link to="/faq" data-testid="footer-faq" className="hover:text-[var(--red)]">FAQ</Link>
            <Link to="/projects" data-testid="footer-projects" className="hover:text-[var(--red)]">Projects</Link>
            <Link to="/contact" data-testid="footer-contact" className="hover:text-[var(--red)]">Get a quote</Link>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
